<script setup lang="ts">
import {
    AUTO_COMPLETE_REQUEST_TYPE,
    SearchBarAutoCompleteApiResponse, SearchBarAutoCompleteItem
} from "@/auto-complete-searchbar/interfaces/ISearchBarAutoComplete";
import {ref} from "vue";
import debounce from "lodash.debounce";
import {searchBarApi} from "@/container";
import {Dialog, DialogTitle, DialogHeader, DialogTrigger, DialogContent} from "@/core/components/ui/dialog";
import PatientFilterChip from "@/prescription/components/PatientFilterChip.vue";
import SearchInput from "@/core/components/search-bar/SearchInput.vue";
import {Table, TableCell, TableRow} from "@/core/components/ui/table";

const props = defineProps({
    title: {
        type: String,
        default: 'Recherche'
    },
    placeholder: {
        type: String,
        default: 'Produit, laboratoire ou gamme ...'
    },
    searchTypes: {
        type: Array as () => AUTO_COMPLETE_REQUEST_TYPE[],
        default: () => [
            AUTO_COMPLETE_REQUEST_TYPE.PRODUCT,
            AUTO_COMPLETE_REQUEST_TYPE.LABORATORY,
            AUTO_COMPLETE_REQUEST_TYPE.RANGE
        ]
    },
    maxResults: {
        type: Number,
        default: 5
    },
    minSearchLength: {
        type: Number,
        default: 3
    },
    debounceDelay: {
        type: Number,
        default: 400
    }
});

const emits = defineEmits<{
    (e: 'selected', type: 'product' | 'laboratory' | 'range' | null, data: SearchBarAutoCompleteItem | null): void;
}>();

const loading = ref<boolean>(false);
const results = ref<SearchBarAutoCompleteApiResponse | null>(null);
const open = ref(false);
const selectedItem = ref<{ type: 'product' | 'laboratory' | 'range'; data: SearchBarAutoCompleteItem } | null>();

/**
 * Debounced search function.
 */
const search = debounce(async (value: string) => {
    if (!value || value.length < props.minSearchLength) {
        clear();
        return;
    }

    loading.value = true;

    results.value = await searchBarApi().autocomplete(
        value,
        props.searchTypes,
        props.maxResults,
    ).catch(() => null);
    loading.value = false;
}, props.debounceDelay);

/**
 * Clear the search results.
 */
const clear = () => {
    results.value = null;
};

/**
 * Emit selection and close the modal.
 */
const select = (type: 'product' | 'laboratory' | 'range', data: SearchBarAutoCompleteItem) => {
    selectedItem.value = {type, data};
    emits('selected', selectedItem.value.type, selectedItem.value.data);

    open.value = false;
};
</script>

<template>
  <Dialog v-model:open="open" @update:open="clear">
    <DialogTrigger>
      <slot name="trigger">
        <PatientFilterChip :selected="!!selectedItem" label="Rechercher >" icon="mdi-magnify" />
      </slot>
    </DialogTrigger>
    <DialogContent>
      <DialogHeader>
        <DialogTitle>{{ title }}</DialogTitle>
      </DialogHeader>
      <div>
        <SearchInput
          :is-loading="loading"
          class="bg-gray-50"
          :placeholder="placeholder"
          @input="search"
        />
      </div>
      <div class="min-h-72 max-h-96 overflow-y-auto">
        <div v-if="results" class="flex flex-col gap-y-3 py-3">
          <div class="flex flex-col gap-y-3">
            <h3>Produits</h3>
            <Table>
              <TableRow
                v-for="product in results.product"
                :key="'product' + product.id"
                class="cursor-pointer"
                @click="select('product', product)"
              >
                <TableCell>{{ product.id }}</TableCell>
                <TableCell>{{ product.cip }}</TableCell>
                <TableCell>{{ product.name }}</TableCell>
              </TableRow>
            </Table>
          </div>
          <div class="flex flex-col gap-y-3">
            <h3>laboratoires</h3>
            <Table>
              <TableRow
                v-for="laboratory in results.laboratory"
                :key="'lab' + laboratory.id"
                class="cursor-pointer"
                @click="select('laboratory', laboratory)"
              >
                <TableCell>{{ laboratory.id }}</TableCell>
                <TableCell>{{ laboratory.name }}</TableCell>
              </TableRow>
            </Table>
          </div>
          <div class="flex flex-col gap-y-3">
            <h3>Gammes</h3>
            <Table>
              <TableRow
                v-for="range in results.ranges"
                :key="'range' + range.id"
                class="cursor-pointer"
                @click="select('range', range)"
              >
                <TableCell>{{ range.id }}</TableCell>
                <TableCell>{{ range.name }}</TableCell>
              </TableRow>
            </Table>
          </div>
        </div>
      </div>
    </DialogContent>
  </Dialog>
</template>

<style scoped>

</style>
